import React from "react";
import Card from "@amzn/meridian/esm/components/card";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme";
import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import alertKnockout from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";

const SummaryCard = ({ summary, onViewMoreClick }) => {
  const MAX_OVERVIEW_CHAR = 110;
  const MAX_TITLE_CHAR = 30;

  const { meetingTitle, meetingDateAndTime, conciseOverview, summaryType, role } = summary;

  const showAlertIcon = summaryType === "privileged_and_confidential";
  const errorTypes = new Set(["server_error", "short_meeting", "investigation", "hr_complaint", "performance_review"]);
  const showErrorIcon = errorTypes.has(summaryType);

  const showOrganizerOnly = summaryType === "privileged_and_confidential" && role !== "host";
  const isTopicNotApproved = ["performance_review", "hr_complaint", "investigation"].includes(summaryType);

  const overviewMessages = {
    organizerOnly: "Contact meeting organizer if you want to see the meeting summary.",
    topicNotApproved: "There is no meeting summary because the meeting topics are not approved for Amazon Meetings Summary.",
    tooShort:
      "There is no meeting summary because the summarization was too short. A meeting summarization needs to take at least 2 minutes to create a summary.",
    notEnoughAudio: "There is no meeting summary because we didn’t hear enough conversation. This may be due to microphone issues.",
    serverError: "There is no meeting summary because there were issues with the server.",
  };

  const getOverview = () => {
    if (showOrganizerOnly) return overviewMessages.organizerOnly;
    if (isTopicNotApproved) return overviewMessages.topicNotApproved;
    if (summaryType === "short_meeting") return overviewMessages.tooShort;
    if (summaryType === "not_enough_audio") return overviewMessages.notEnoughAudio;
    if (summaryType === "server_error") return overviewMessages.serverError;
    return conciseOverview;
  };

  const getTrimmedOverview = () => {
    const overview = getOverview();
    if (overview.length <= MAX_OVERVIEW_CHAR) return overview;
    return overview.slice(0, MAX_OVERVIEW_CHAR).trimEnd() + "…more";
  };

  //TODO: make it more fit
  const getTrimmedTitle = () => {
    if (meetingTitle.length <= MAX_TITLE_CHAR) return meetingTitle;
    return meetingTitle.slice(0, MAX_TITLE_CHAR).trimEnd() + "…";
  };

  const renderStatusIcon = (iconToken, color) => (
    <Column spacing="none" spacingInset="none" alignmentVertical="top" alignmentHorizontal="start">
      <Column>
        <Column height="8px" />
        <Column height="16px">
          <Icon tokens={iconToken} color={color} />
        </Column>
        <Column height="138px" />
      </Column>
    </Column>
  );

  return (
    <Theme tokens={{ cardDropShadowDefaultBlur: "0px" }}>
      <Card width="306px" spacingInset="none">
        <Row widths={["24px", "256px", "20px"]} height="162px" spacing="none" spacingInset="none">
          <Row width="24px" />
          <Column spacing="none" spacingInset="none" alignmentHorizontal="start">
            <Column height="16px" />
            <Box spacing="none" spacingInset="none" minHeight={18} width="256px">
              <>
                <style>
                  {`
                    @font-face {
                      font-family: 'Amazon Ember Serif';
                      src: url("https://m.media-amazon.com/images/G/01/kindle/rewards/home/2022_beta/fonts/AmazonEmberSerif_Bd.woff") format("woff"), 
                        url("https://m.media-amazon.com/images/G/01/kindle/rewards/home/2022_beta/fonts/AmazonEmberSerif_Bd.woff") format("woff2");
                      font-weight: 700;
                    }
                  `}
                </style>
                <Theme tokens={{ textDefaultFontFamily: ["Amazon Ember Serif"] }}>
                  <Text type="h50">
                    {getTrimmedTitle()}
                  </Text>
                </Theme>
              </>
            </Box>
            <Column height="8px" />
            <Row spacing="none" spacingInset="none">
              <Icon tokens={calendarTokens} color="secondary" />
              <Row width="4px" />
              <Text type="b100" className="summary-time" color="secondary">
                {meetingDateAndTime}
              </Text>
            </Row>
            <Column height="16px" />

            {/*Overview box*/}
            <Box width="260px" height="48px" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <Text type="b100" color="secondary">
                {getTrimmedOverview()}
              </Text>
            </Box>

            <Column height="8px" />
            <Box height="16px" width="98px" spacing="none" spacingInset="none">
              <Theme tokens={{ textColorPrimary: "#006D5B" }}>
                <Link onClick={() => onViewMoreClick(summary)} type="secondary">
                  <Text color="primary" type="b100" fontFamily="amazonEmber">
                    View more
                  </Text>
                </Link>
              </Theme>
            </Box>
            <Column height="14px" />
          </Column>

          {/*Show alert ot error icon for some meetingType*/}
          {showAlertIcon && renderStatusIcon(alertKnockout, "warning")}
          {showErrorIcon && renderStatusIcon(errorKnockout, "error")}
        </Row>
      </Card>
    </Theme>
  );
};

export default SummaryCard;
